import { useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import posthog from 'posthog-js'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import SearchableSelect from '@/components/SearchableSelect'
import { COUNTRIES } from '@/utils/countries'
import { getCurrentAgreement, CURRENT_AGREEMENT_VERSION } from '@/utils/supplierAgreement'

interface BokunVendorPreview {
  vendorId: number
  companyName: string
  businessType: string
  productCount: number
}

export default function VendorSignUpPage() {
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()

  // Bokun onboarding state -- Bokun is the only signup path right now
  const [signupMode, setSignupMode] = useState<'choose' | 'bokun' | 'standard'>('bokun')
  const [bokunVendorId, setBokunVendorId] = useState('')
  const [bokunPreview, setBokunPreview] = useState<BokunVendorPreview | null>(null)
  const [bokunError, setBokunError] = useState('')
  const [verifying, setVerifying] = useState(false)

  // Business Information
  const [businessName, setBusinessName] = useState('')
  const [businessType, setBusinessType] = useState<'tours' | 'accommodation' | 'transfers' | 'activities'>('tours')
  // No default — a silent 'company' sends sole traders into Stripe's company
  // KYC, which asks for incorporation documents they do not have. Stripe fixes
  // business_type once onboarding starts, so a wrong guess means deleting the
  // connected account and starting over. Make the vendor choose.
  const [legalEntityType, setLegalEntityType] = useState<'individual' | 'company' | ''>('')
  const [taxId, setTaxId] = useState('')
  const [vatId, setVatId] = useState('')
  const [registrationNumber, setRegistrationNumber] = useState('')
  const [website, setWebsite] = useState('')
  const [username, setUsername] = useState('')

  // Contact Person
  const [contactPersonName, setContactPersonName] = useState('')
  const [contactPersonEmail, setContactPersonEmail] = useState('')
  const [contactPersonPhone, setContactPersonPhone] = useState('')

  // Address
  const [street, setStreet] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [country, setCountry] = useState('')

  // Authentication
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Supplier Agreement
  const [agreementAccepted, setAgreementAccepted] = useState(false)
  const [agreementScrolledToBottom, setAgreementScrolledToBottom] = useState(false)
  const agreementRef = useRef<HTMLDivElement>(null)

  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [currentStep, setCurrentStep] = useState(1)

  const agreement = getCurrentAgreement()

  const handleAgreementScroll = useCallback(() => {
    const el = agreementRef.current
    if (!el) return
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30
    if (isAtBottom) setAgreementScrolledToBottom(true)
  }, [])

  const handleVerifyBokun = async () => {
    if (!bokunVendorId.trim()) {
      setBokunError('Please enter your Bokun vendor ID')
      return
    }

    setVerifying(true)
    setBokunError('')

    try {
      const response = await api.auth.verifyBokunVendor(bokunVendorId.trim())
      const result = response.data.data

      if (!result.found) {
        setBokunError('No vendor found with this Bokun ID. Please check the ID and try again.')
        return
      }

      if (result.claimed) {
        setBokunError('This vendor already has an account. Please sign in instead.')
        return
      }

      // Pre-fill form with Bokun data
      setBokunPreview(result.vendor)
      setBusinessName(result.vendor.companyName)
      setBusinessType(result.vendor.businessType || 'tours')
      setUsername(result.vendor.companyName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''))
      setSignupMode('bokun')
      posthog.capture('vendor_signup_bokun_verified', { bokun_vendor_id: bokunVendorId.trim() })
    } catch (err: any) {
      const msg = err.response?.data?.error?.message || err.response?.data?.message || 'Failed to verify Bokun ID'
      setBokunError(msg)
      posthog.capture('vendor_signup_bokun_failed', { bokun_vendor_id: bokunVendorId.trim(), error: msg })
    } finally {
      setVerifying(false)
    }
  }

  const handleAuthResponse = (response: any) => {
    const { user, token } = response.data.data
    const mappedUser = {
      id: user.userId,
      email: user.email,
      name: `${user.firstName} ${user.lastName}`.trim(),
      role: user.role,
      vendorId: user.tenantId,
      subscriptionTier: user.subscriptionTier ?? 0,
    }
    setAuth(mappedUser, token)
    posthog.identify(mappedUser.id, {
      email: mappedUser.email,
      name: mappedUser.name,
      role: mappedUser.role,
      vendor_id: mappedUser.vendorId,
      subscription_tier: mappedUser.subscriptionTier,
    })
    if (mappedUser.vendorId) {
      posthog.group('vendor', mappedUser.vendorId)
    }
    posthog.capture('vendor_signup_completed', { mode: signupMode, vendor_id: mappedUser.vendorId })
    navigate('/vendor/onboarding')
  }

  const handleAuthError = (err: any) => {
    if (!err.response) {
      const networkError = err.message || 'Network error'
      if (networkError.includes('Network Error') || networkError.includes('Failed to fetch')) {
        setError('Unable to connect to server. Please check if the API is running and try again.')
      } else {
        setError(`Connection error: ${networkError}`)
      }
      setFieldErrors({})
      posthog.capture('vendor_signup_failed', { mode: signupMode, error: networkError })
      return
    }

    const errorData = err.response?.data?.error
    const generalMessage = errorData?.message || err.response?.data?.message || `Registration failed: ${err.response?.statusText || 'Unknown error'}`
    setError(generalMessage)
    posthog.capture('vendor_signup_failed', { mode: signupMode, error: generalMessage })

    if (errorData?.details && Array.isArray(errorData.details)) {
      const errors: Record<string, string> = {}
      errorData.details.forEach((detail: any) => {
        const fieldName = detail.field.split('.').pop() || detail.field
        errors[fieldName] = detail.message.replace(/"/g, '')
      })
      setFieldErrors(errors)

      const errorFields = Object.keys(errors)
      if (errorFields.some(f => ['businessName', 'businessType', 'taxId', 'vatId', 'registrationNumber', 'website', 'username'].includes(f))) {
        setCurrentStep(1)
      } else if (errorFields.some(f => ['contactPersonName', 'contactPersonEmail', 'contactPersonPhone', 'street', 'city', 'state', 'postalCode', 'country'].includes(f))) {
        setCurrentStep(2)
      } else if (errorFields.some(f => ['password'].includes(f))) {
        setCurrentStep(3)
      }
    } else {
      setFieldErrors({})
    }
  }

  const signUpMutation = useMutation({
    mutationFn: (data: any) => api.auth.signUpVendor(data),
    onSuccess: handleAuthResponse,
    onError: handleAuthError,
  })

  const signUpBokunMutation = useMutation({
    mutationFn: (data: any) => api.auth.signUpWithBokun(data),
    onSuccess: handleAuthResponse,
    onError: handleAuthError,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setFieldErrors({})

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      setFieldErrors({ confirmPassword: 'Passwords do not match' })
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      setFieldErrors({ password: 'Password must be at least 8 characters' })
      return
    }

    if (legalEntityType !== 'individual' && legalEntityType !== 'company') {
      setError('Please tell us whether you are signing up as an individual or a registered company')
      setFieldErrors({ legalEntityType: 'Select individual or registered company' })
      return
    }

    if (signupMode === 'bokun' && bokunPreview) {
      signUpBokunMutation.mutate({
        bokunVendorId: Number(bokunVendorId),
        companyName: businessName,
        legalName: businessName,
        businessType,
        legalEntityType,
        registrationNumber: legalEntityType === 'company' ? registrationNumber || undefined : undefined,
        taxId: taxId || undefined,
        vatId: vatId || undefined,
        website: website || undefined,
        username,
        contactPersonName,
        contactPersonEmail,
        contactPersonPhone,
        address: {
          street,
          city,
          state: state || undefined,
          postalCode,
          country,
        },
        password,
        agreementVersion: CURRENT_AGREEMENT_VERSION,
        agreementAccepted: true,
      })
    } else {
      signUpMutation.mutate({
        businessName,
        legalName: businessName,
        businessType,
        taxId,
        vatId: vatId || undefined,
        registrationNumber: registrationNumber || undefined,
        website: website || undefined,
        contactPersonName,
        contactPersonEmail,
        contactPersonPhone,
        address: {
          street,
          city,
          state: state || undefined,
          postalCode,
          country,
        },
        password,
        agreementVersion: CURRENT_AGREEMENT_VERSION,
        agreementAccepted: true,
      })
    }
  }

  const isPending = signUpMutation.isPending || signUpBokunMutation.isPending

  const nextStep = () => {
    if (currentStep < 3) setCurrentStep(currentStep + 1)
  }

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1)
  }

  const getInputStyle = (fieldName: string) => ({
    width: '100%',
    border: fieldErrors[fieldName] ? '2px solid #dc3545' : '1px solid #ccc',
    borderRadius: '4px',
    padding: '0.5rem',
  })

  const renderFieldError = (fieldName: string) => {
    if (!fieldErrors[fieldName]) return null
    return (
      <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
        {fieldErrors[fieldName]}
      </div>
    )
  }

  // ─── Choose signup mode ──────────────────────────────────────────────────
  if (signupMode === 'choose') {
    return (
      <div className="container" style={{ maxWidth: '700px', margin: '2rem auto', padding: '2rem' }}>
        <div className="card">
          <h1 style={{ textAlign: 'center', marginBottom: '1rem' }}>Register Your Business</h1>
          <p style={{ textAlign: 'center', color: '#666', marginBottom: '2rem' }}>
            Start sharing amazing experiences with travelers.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Bokun option */}
            <button
              type="button"
              onClick={() => { setSignupMode('bokun'); posthog.capture('vendor_signup_started', { mode: 'bokun' }) }}
              style={{
                width: '100%',
                padding: '1.5rem',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                background: '#fff',
                color: 'var(--color-gray-900)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.25rem' }}>
                Join with Bokun
              </div>
              <div style={{ color: '#666', fontSize: '0.9rem' }}>
                Already have products on Bokun? Enter your vendor ID to link your account and import your catalog.
              </div>
            </button>

            {/* Standard option */}
            <button
              type="button"
              onClick={() => { setSignupMode('standard'); setCurrentStep(1); posthog.capture('vendor_signup_started', { mode: 'standard' }) }}
              style={{
                width: '100%',
                padding: '1.5rem',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                background: '#fff',
                color: 'var(--color-gray-900)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.25rem' }}>
                Create New Account
              </div>
              <div style={{ color: '#666', fontSize: '0.9rem' }}>
                New to Tixxly? Register your business from scratch.
              </div>
            </button>
          </div>

          <div style={{ textAlign: 'center', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
            <button
              onClick={() => navigate('/auth')}
              style={{
                background: 'transparent',
                color: 'var(--primary-color)',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Already have an account? Sign In
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ─── Bokun verification step (before wizard) ─────────────────────────────
  if (signupMode === 'bokun' && !bokunPreview) {
    return (
      <div className="container" style={{ maxWidth: '700px', margin: '2rem auto', padding: '2rem' }}>
        <div className="card">
          <h1 style={{ textAlign: 'center', marginBottom: '1rem' }}>Join with Bokun</h1>
          <p style={{ textAlign: 'center', color: '#666', marginBottom: '2rem' }}>
            Enter your Bokun vendor ID to find your account and import your products.
          </p>

          {bokunError && <div className="error-message" style={{ marginBottom: '1rem' }}>{bokunError}</div>}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label htmlFor="bokunVendorId" style={{ display: 'block', marginBottom: '0.5rem' }}>
                Bokun Vendor ID *
              </label>
              <input
                id="bokunVendorId"
                type="text"
                value={bokunVendorId}
                onChange={(e) => setBokunVendorId(e.target.value)}
                placeholder="e.g., 40577"
                style={{ width: '100%', border: '1px solid #ccc', borderRadius: '4px', padding: '0.5rem' }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyBokun() }}
              />
              <div style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                You can find this in your Bokun dashboard under Account Settings.
              </div>
            </div>

            <button
              type="button"
              onClick={handleVerifyBokun}
              disabled={verifying}
              style={{ width: '100%' }}
            >
              {verifying ? 'Verifying...' : 'Verify Bokun ID'}
            </button>

            <button
              type="button"
              onClick={() => { setSignupMode('choose'); setBokunError('') }}
              style={{
                width: '100%',
                background: 'transparent',
                color: 'var(--primary-color)',
                border: '1px solid #ccc',
              }}
            >
              Back
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ─── Main registration form (3-step wizard) ──────────────────────────────
  return (
    <div className="container" style={{ maxWidth: '700px', margin: '2rem auto', padding: '2rem' }}>
      <div className="card">
        <h1 style={{ textAlign: 'center', marginBottom: '1rem' }}>
          {signupMode === 'bokun' ? 'Complete Your Registration' : 'Register Your Business'}
        </h1>

        {/* Bokun preview banner */}
        {signupMode === 'bokun' && bokunPreview && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            borderRadius: '4px',
            border: '1px solid #c3e6cb',
            marginBottom: '1.5rem',
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
              Bokun account found: {bokunPreview.companyName}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#155724' }}>
              {bokunPreview.productCount} product{bokunPreview.productCount !== 1 ? 's' : ''} ready to import.
              Complete the form below to claim your account.
            </div>
          </div>
        )}

        {!bokunPreview && (
          <p style={{ textAlign: 'center', color: '#666', marginBottom: '2rem' }}>
            Start sharing amazing experiences with travelers. Let's get your business set up.
          </p>
        )}

        {/* Progress Indicator */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem', gap: '1rem' }}>
          {[1, 2, 3].map((step) => (
            <div key={step} style={{
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              backgroundColor: currentStep >= step ? '#007bff' : '#ddd',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>{step}</div>
          ))}
        </div>

        {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Step 1: Business Information */}
          {currentStep === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <h3>Business Information</h3>

              <div>
                <label htmlFor="businessName" style={{ display: 'block', marginBottom: '0.5rem' }}>
                  Business Name *
                  <span style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)', fontWeight: 400, marginLeft: '0.5rem' }}>
                    (from Bokun)
                  </span>
                </label>
                <input
                  id="businessName"
                  type="text"
                  value={businessName}
                  readOnly
                  required
                  style={{ ...getInputStyle('businessName'), background: 'var(--color-gray-100)', cursor: 'not-allowed' }}
                />
                {renderFieldError('businessName')}
              </div>

              <div>
                <label htmlFor="businessType" style={{ display: 'block', marginBottom: '0.5rem' }}>Business Type *</label>
                <select id="businessType" value={businessType} onChange={(e) => setBusinessType(e.target.value as any)} required style={{ width: '100%' }}>
                  <option value="tours">Tours</option>
                  <option value="accommodation">Accommodation</option>
                  <option value="transfers">Transfers</option>
                  <option value="activities">Activities</option>
                </select>
              </div>

              <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                <legend style={{ display: 'block', marginBottom: '0.5rem' }}>I'm signing up as *</legend>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input type="radio" name="legalEntityType" value="individual" checked={legalEntityType === 'individual'} onChange={() => setLegalEntityType('individual')} />
                    Individual / sole trader
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input type="radio" name="legalEntityType" value="company" checked={legalEntityType === 'company'} onChange={() => setLegalEntityType('company')} />
                    Registered company
                  </label>
                </div>
                <div style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                  Self-employed (e.g. autónomo, sole proprietor)? Choose Individual. Stripe will then ask for your personal ID instead of company documents.
                </div>
              </fieldset>

              <div>
                <label htmlFor="taxId" style={{ display: 'block', marginBottom: '0.5rem' }}>
                  {legalEntityType === 'company' ? 'Tax ID *' : 'Tax ID (optional)'}
                </label>
                <input id="taxId" type="text" value={taxId} onChange={(e) => setTaxId(e.target.value)} required={legalEntityType === 'company'} placeholder={legalEntityType === 'company' ? 'e.g., EIN 12-3456789' : 'e.g., NIF, UTR or SSN'} style={getInputStyle('taxId')} />
                {renderFieldError('taxId')}
              </div>

              <div>
                <label htmlFor="vatId" style={{ display: 'block', marginBottom: '0.5rem' }}>VAT Number</label>
                <input id="vatId" type="text" value={vatId} onChange={(e) => setVatId(e.target.value)} placeholder="e.g., GB123456789" style={getInputStyle('vatId')} />
                {renderFieldError('vatId')}
              </div>

              {legalEntityType === 'company' && (
                <div>
                  <label htmlFor="registrationNumber" style={{ display: 'block', marginBottom: '0.5rem' }}>Company Registration Number</label>
                  <input id="registrationNumber" type="text" value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} placeholder="Company reg number (optional)" style={getInputStyle('registrationNumber')} />
                  {renderFieldError('registrationNumber')}
                </div>
              )}

              {signupMode === 'bokun' && (
                <div>
                  <label htmlFor="username" style={{ display: 'block', marginBottom: '0.5rem' }}>Username (website alias) *</label>
                  <input id="username" type="text" value={username} onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} required placeholder="your-business-name" style={getInputStyle('username')} />
                  <div style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                    Your storefront URL:{' '}
                    {username ? (
                      <a href={`https://stores.tixxly.ai/en/v/${username}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-tixxly-navy)' }}>
                        stores.tixxly.ai/v/{username}
                      </a>
                    ) : 'stores.tixxly.ai/v/...'}
                  </div>
                  {renderFieldError('username')}
                </div>
              )}

              <div>
                <label htmlFor="website" style={{ display: 'block', marginBottom: '0.5rem' }}>Website</label>
                <input id="website" type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://www.yourwebsite.com" style={getInputStyle('website')} />
                {renderFieldError('website')}
              </div>

              <button type="button" onClick={nextStep} style={{ width: '100%', marginTop: '1rem' }}>
                Next: Contact Information
              </button>
            </div>
          )}

          {/* Step 2: Contact Person & Address */}
          {currentStep === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <h3>Contact Person</h3>

              <div>
                <label htmlFor="contactPersonName" style={{ display: 'block', marginBottom: '0.5rem' }}>Full Name *</label>
                <input id="contactPersonName" type="text" value={contactPersonName} onChange={(e) => setContactPersonName(e.target.value)} required placeholder="John Smith" style={getInputStyle('contactPersonName')} />
                {renderFieldError('contactPersonName')}
              </div>

              <div>
                <label htmlFor="contactPersonEmail" style={{ display: 'block', marginBottom: '0.5rem' }}>Email Address *</label>
                <input id="contactPersonEmail" type="email" value={contactPersonEmail} onChange={(e) => setContactPersonEmail(e.target.value)} required placeholder="john@acmetours.com" style={getInputStyle('contactPersonEmail')} />
                {renderFieldError('contactPersonEmail')}
              </div>

              <div>
                <label htmlFor="contactPersonPhone" style={{ display: 'block', marginBottom: '0.5rem' }}>Phone Number *</label>
                <input id="contactPersonPhone" type="tel" value={contactPersonPhone} onChange={(e) => setContactPersonPhone(e.target.value)} required placeholder="+44 20 1234 5678" style={getInputStyle('contactPersonPhone')} />
                {renderFieldError('contactPersonPhone')}
              </div>

              <h3 style={{ marginTop: '1rem' }}>Business Address</h3>

              <div>
                <label htmlFor="street" style={{ display: 'block', marginBottom: '0.5rem' }}>Street Address *</label>
                <input id="street" type="text" value={street} onChange={(e) => setStreet(e.target.value)} required placeholder="123 Main Street" style={getInputStyle('street')} />
                {renderFieldError('street')}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem' }}>
                <div>
                  <label htmlFor="city" style={{ display: 'block', marginBottom: '0.5rem' }}>City *</label>
                  <input id="city" type="text" value={city} onChange={(e) => setCity(e.target.value)} required placeholder="London" style={getInputStyle('city')} />
                  {renderFieldError('city')}
                </div>
                <div>
                  <label htmlFor="state" style={{ display: 'block', marginBottom: '0.5rem' }}>State/Province</label>
                  <input id="state" type="text" value={state} onChange={(e) => setState(e.target.value)} placeholder="Optional" style={getInputStyle('state')} />
                  {renderFieldError('state')}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem' }}>
                <div>
                  <label htmlFor="postalCode" style={{ display: 'block', marginBottom: '0.5rem' }}>Postal Code *</label>
                  <input id="postalCode" type="text" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} required placeholder="SW1A 1AA" style={getInputStyle('postalCode')} />
                  {renderFieldError('postalCode')}
                </div>
                <div>
                  <label htmlFor="country" style={{ display: 'block', marginBottom: '0.5rem' }}>Country *</label>
                  <SearchableSelect
                    id="country"
                    value={country}
                    onChange={setCountry}
                    options={COUNTRIES}
                    placeholder="Search country..."
                    required
                    style={getInputStyle('country')}
                  />
                  {renderFieldError('country')}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem', marginTop: '1rem' }}>
                <button type="button" onClick={prevStep} style={{ width: '100%' }}>Back</button>
                <button type="button" onClick={nextStep} style={{ width: '100%' }}>Next: Account Setup</button>
              </div>
            </div>
          )}

          {/* Step 3: Account Setup */}
          {currentStep === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <h3>Account Setup</h3>

              <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px', marginBottom: '1rem' }}>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>
                  You'll use <strong>{contactPersonEmail || 'your email'}</strong> to sign in
                </p>
              </div>

              <div>
                <label htmlFor="password" style={{ display: 'block', marginBottom: '0.5rem' }}>Password *</label>
                <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} placeholder="At least 8 characters" style={getInputStyle('password')} />
                {renderFieldError('password')}
              </div>

              <div>
                <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: '0.5rem' }}>Confirm Password *</label>
                <input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} placeholder="Re-enter password" style={getInputStyle('confirmPassword')} />
                {renderFieldError('confirmPassword')}
              </div>

              {/* Supplier Agreement -- required for both signup modes */}
              {(
                <>
                  <h3 style={{ marginTop: '1rem', marginBottom: '0.25rem' }}>Supplier Agreement</h3>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                    Please read the agreement below before creating your account.
                  </p>

                  <div
                    ref={agreementRef}
                    onScroll={handleAgreementScroll}
                    style={{
                      maxHeight: '300px',
                      overflowY: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '1rem',
                      backgroundColor: '#fafafa',
                      fontSize: '0.8rem',
                      lineHeight: '1.6',
                    }}
                  >
                    <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                      <strong style={{ fontSize: '1rem' }}>{agreement.title}</strong>
                      <div style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        Version {agreement.version} | Effective {agreement.effectiveDate}
                      </div>
                    </div>

                    {agreement.sections.map((section, i) => (
                      <div key={i} style={{ marginBottom: '1rem' }}>
                        <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{section.heading}</strong>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{section.body}</div>
                      </div>
                    ))}

                    <hr style={{ margin: '1.5rem 0', borderColor: '#ddd' }} />

                    <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                      <strong style={{ fontSize: '1rem' }}>{agreement.dpaTitle}</strong>
                      <div style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        Appendix to the Supplier Services Agreement
                      </div>
                    </div>

                    {agreement.dpaSections.map((section, i) => (
                      <div key={`dpa-${i}`} style={{ marginBottom: '1rem' }}>
                        <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{section.heading}</strong>
                        <div style={{ whiteSpace: 'pre-wrap' }}>{section.body}</div>
                      </div>
                    ))}

                    <hr style={{ margin: '1.5rem 0', borderColor: '#ddd' }} />

                    <div style={{ marginBottom: '1rem' }}>
                      <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{agreement.annexTitle}</strong>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{agreement.annexBody}</div>
                    </div>
                  </div>

                  {!agreementScrolledToBottom && (
                    <div style={{ fontSize: '0.8rem', color: '#888' }}>
                      Please scroll through the entire agreement to continue.
                    </div>
                  )}

                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: agreementScrolledToBottom ? 'pointer' : 'not-allowed' }}>
                    <input
                      type="checkbox"
                      checked={agreementAccepted}
                      onChange={(e) => setAgreementAccepted(e.target.checked)}
                      disabled={!agreementScrolledToBottom}
                      style={{ marginTop: '0.2rem' }}
                    />
                    <span style={{ fontSize: '0.85rem', color: agreementScrolledToBottom ? '#333' : '#999' }}>
                      I have read and agree to the Supplier Services Agreement (Version {agreement.version})
                    </span>
                  </label>
                  {renderFieldError('agreementAccepted')}
                </>
              )}

              <div style={{ padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107', marginTop: '1rem' }}>
                <p style={{ margin: 0, fontSize: '0.85rem' }}>
                  Your business will be reviewed and validated by our team before you can start selling.
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem', marginTop: '1rem' }}>
                <button type="button" onClick={prevStep} style={{ width: '100%' }}>Back</button>
                <button
                  type="submit"
                  disabled={isPending || !agreementAccepted}
                  style={{ width: '100%' }}
                >
                  {isPending
                    ? 'Creating Account...'
                    : signupMode === 'bokun'
                      ? 'Accept & Claim Account'
                      : 'Accept & Create Account'}
                </button>
              </div>
            </div>
          )}
        </form>

        <div style={{ textAlign: 'center', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
          <button
            onClick={() => {
              setSignupMode('choose')
              setBokunPreview(null)
              setBokunError('')
              setCurrentStep(1)
            }}
            style={{
              background: 'transparent',
              color: 'var(--primary-color)',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Back to signup options
          </button>
        </div>
      </div>
    </div>
  )
}
